import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, Package, ExternalLink, Truck, RefreshCw, Mail, PackageCheck, Pencil, MapPin } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending_forward: { label: "Needs action", cls: "bg-[#D9923B]/15 text-[#8a5a1a]" },
  forwarded: { label: "Sent to CJ", cls: "bg-[#2A3D36]/15 text-[#2A3D36]" },
  shipped: { label: "Shipped", cls: "bg-[#4A5D23]/15 text-[#4A5D23]" },
  delivered: { label: "Delivered", cls: "bg-[#4A5D23]/20 text-[#3a4a1a]" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700" },
};

const OrderCard = ({ order, onChanged }) => {
  const [placing, setPlacing] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [tracking, setTracking] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    buyer_name: order.buyer_name || "",
    buyer_phone: order.buyer_phone || "",
    buyer_address: order.buyer_address || "",
    ship_city: order.ship_city || "",
    ship_province: order.ship_province || "",
    ship_zip: order.ship_zip || "",
    buyer_country: order.buyer_country || "",
  });
  const isCJ = (order.supplier_url || "").toUpperCase().includes("CJ") || !!order.cj_order_id;
  const st = STATUS[order.status] || STATUS.pending_forward;
  const margin = (Number(order.buyer_paid || 0) - Number(order.supplier_cost || 0)).toFixed(2);
  const missingCountry = !order.buyer_country;

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveShipping = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/supplier-orders/${order.supplier_order_id}`, form);
      toast.success("Shipping address saved");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save shipping");
    } finally {
      setSaving(false);
    }
  };

  const sendToCJ = async () => {
    if (missingCountry) {
      setEditing(true);
      toast.error("Add the shipping country first, then Send to CJ");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post(`/admin/cj/supplier-orders/${order.supplier_order_id}/place`);
      toast.success(`Created CJ order #${data.cj_order_id}. Now open CJ → My Orders and PAY it.`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send to CJ");
    } finally {
      setPlacing(false);
    }
  };

  const markShipped = async () => {
    if (!tracking.trim()) { toast.error("Enter the tracking number first"); return; }
    setShipping(true);
    try {
      await api.post(`/admin/supplier-orders/${order.supplier_order_id}/ship`, { tracking_number: tracking.trim() });
      toast.success("Marked shipped — buyer emailed the tracking");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark shipped");
    } finally {
      setShipping(false);
    }
  };

  return (
    <Card className="p-4 bg-white border-black/10" data-testid={`fulfil-order-${order.supplier_order_id}`}>
      <div className="flex items-start gap-4">
        {order.listing_image && (
          <img src={order.listing_image} alt="" className="w-16 h-16 rounded-lg object-cover border border-black/5 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-[#1A2024] text-sm line-clamp-2">{order.listing_title}</div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                <Mail className="w-3 h-3" />
                <span>{order.buyer_name || order.buyer_email}</span>
                <span>·</span>
                <span>{order.buyer_country || "?"}</span>
                <span>·</span>
                <span>Qty {order.qty || 1}</span>
                {order.variant_size && <span>· Size {order.variant_size}</span>}
                {order.variant_color && <span>· {order.variant_color}</span>}
              </div>
            </div>
            <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
          </div>

          <div className="text-xs mt-2 text-muted-foreground">
            Buyer paid <strong className="text-[#1A2024]">{order.currency?.toUpperCase()} {order.buyer_paid}</strong>
            {order.supplier_cost > 0 && <> · your margin <strong className="text-[#4A5D23]">£{margin}</strong></>}
          </div>

          {/* Shipping address */}
          <div className="mt-2 text-xs">
            <div className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="w-3 h-3" />
              {order.buyer_address || order.ship_city || order.ship_zip ? (
                <span className="truncate">
                  {[order.buyer_address, order.ship_city, order.ship_province, order.ship_zip, order.buyer_country].filter(Boolean).join(", ")}
                </span>
              ) : (
                <span className={missingCountry ? "text-red-600 font-medium" : ""}>No shipping address on file</span>
              )}
              <button onClick={() => setEditing((v) => !v)} data-testid={`fulfil-edit-shipping-${order.supplier_order_id}`} className="ml-1 inline-flex items-center gap-0.5 text-[#C05A3C] hover:underline shrink-0">
                <Pencil className="w-3 h-3" /> {editing ? "Close" : "Edit"}
              </button>
            </div>
            {missingCountry && !editing && (
              <div className="mt-1 text-red-600" data-testid={`fulfil-missing-country-${order.supplier_order_id}`}>
                ⚠️ Missing country — click Edit to add it before sending to CJ.
              </div>
            )}
          </div>

          {editing && (
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid={`fulfil-shipping-form-${order.supplier_order_id}`}>
              <Input value={form.buyer_name} onChange={setF("buyer_name")} placeholder="Recipient name" className="h-8 text-xs col-span-2" data-testid={`fulfil-ship-name-${order.supplier_order_id}`} />
              <Input value={form.buyer_address} onChange={setF("buyer_address")} placeholder="Street address" className="h-8 text-xs col-span-2" data-testid={`fulfil-ship-address-${order.supplier_order_id}`} />
              <Input value={form.ship_city} onChange={setF("ship_city")} placeholder="City" className="h-8 text-xs" data-testid={`fulfil-ship-city-${order.supplier_order_id}`} />
              <Input value={form.ship_province} onChange={setF("ship_province")} placeholder="State / Province" className="h-8 text-xs" data-testid={`fulfil-ship-province-${order.supplier_order_id}`} />
              <Input value={form.ship_zip} onChange={setF("ship_zip")} placeholder="Postcode / ZIP" className="h-8 text-xs" data-testid={`fulfil-ship-zip-${order.supplier_order_id}`} />
              <Input value={form.buyer_country} onChange={setF("buyer_country")} placeholder="Country (e.g. GB, US)" className="h-8 text-xs" data-testid={`fulfil-ship-country-${order.supplier_order_id}`} />
              <Input value={form.buyer_phone} onChange={setF("buyer_phone")} placeholder="Phone" className="h-8 text-xs col-span-2" data-testid={`fulfil-ship-phone-${order.supplier_order_id}`} />
              <Button size="sm" onClick={saveShipping} disabled={saving} data-testid={`fulfil-save-shipping-${order.supplier_order_id}`} className="col-span-2 h-8 bg-[#4A5D23] hover:bg-[#3a4a1a] text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />}
                Save shipping address
              </Button>
            </div>
          )}

          {order.cj_order_id && (
            <div className="text-xs mt-1 font-mono text-[#4A5D23]">CJ order #{order.cj_order_id}</div>
          )}
          {order.cj_place_error && !order.cj_order_id && (
            <div className="text-xs mt-1 text-red-600">CJ error: {order.cj_place_error}</div>
          )}
          {order.tracking_number && (
            <div className="text-xs mt-1 font-mono text-muted-foreground">Tracking: {order.tracking_carrier ? `${order.tracking_carrier} · ` : ""}{order.tracking_number}</div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {isCJ && !order.cj_order_id && order.status !== "shipped" && order.status !== "delivered" && (
              <Button size="sm" onClick={sendToCJ} disabled={placing} data-testid={`fulfil-send-cj-${order.supplier_order_id}`} className="bg-[#C05A3C] hover:bg-[#a84a30] text-white h-8">
                {placing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
                Send to CJ
              </Button>
            )}
            {isCJ && (
              <a href="https://app.cjdropshipping.com/#/order/manage" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-[#C05A3C] hover:underline h-8" data-testid={`fulfil-pay-cj-${order.supplier_order_id}`}>
                <ExternalLink className="w-4 h-4" /> Pay on CJ
              </a>
            )}
            {order.status !== "shipped" && order.status !== "delivered" && (
              <div className="flex items-center gap-1">
                <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking #" className="h-8 w-36 text-xs" data-testid={`fulfil-tracking-${order.supplier_order_id}`} />
                <Button size="sm" variant="outline" onClick={markShipped} disabled={shipping} data-testid={`fulfil-ship-${order.supplier_order_id}`} className="h-8 border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23]/10">
                  {shipping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}
                  Mark shipped
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const ShippingSettingsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/shipping-settings").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/shipping-settings", cfg);
      toast.success("Shipping settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };
  if (!cfg) return null;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  return (
    <Card className="p-4 bg-white border-black/10 mb-4" data-testid="shipping-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <Truck className="w-4 h-4 text-[#C05A3C]" />
        <h2 className="font-display text-lg">Buyer shipping fee</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Charged to buyers on dropship orders so you never pay shipping out of pocket. Live CJ rates are used automatically when available; otherwise this flat fee applies.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-[11px] font-semibold text-muted-foreground">Fee per order ({cfg.currency})</label>
          <Input type="number" step="0.01" value={cfg.flat_fee} onChange={(e) => set("flat_fee", parseFloat(e.target.value) || 0)} className="h-9 mt-1" data-testid="shipping-flat-fee" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-muted-foreground">Extra per item</label>
          <Input type="number" step="0.01" value={cfg.per_extra_item} onChange={(e) => set("per_extra_item", parseFloat(e.target.value) || 0)} className="h-9 mt-1" data-testid="shipping-extra-fee" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-muted-foreground">Currency</label>
          <select value={cfg.currency} onChange={(e) => set("currency", e.target.value)} className="w-full h-9 mt-1 px-2 rounded-md border border-black/15 text-sm" data-testid="shipping-currency">
            {["GBP", "USD", "EUR", "CAD"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Button onClick={save} disabled={saving} data-testid="shipping-settings-save" className="h-9 bg-[#4A5D23] hover:bg-[#3a4a1a] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
      <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer" data-testid="shipping-use-live-toggle">
        <input type="checkbox" checked={!!cfg.use_live_cj} onChange={(e) => set("use_live_cj", e.target.checked)} />
        Use CJ's live shipping rate automatically when available (falls back to the fee above)
      </label>
    </Card>
  );
};

const FulfilOrders = () => {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/supplier-orders");
      setOrders(Array.isArray(data) ? data : (data.orders || []));
    } catch (e) {
      toast.error("Could not load orders");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const needsAction = orders.filter((o) => o.status === "pending_forward" || (!o.cj_order_id && (o.supplier_url || "").toUpperCase().includes("CJ")));

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="fulfil-orders-page">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between gap-4 mb-2">
          <h1 className="font-display text-3xl tracking-tight flex items-center gap-2">
            <PackageCheck className="w-7 h-7 text-[#C05A3C]" /> Orders to Fulfil
          </h1>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="fulfil-refresh" className="h-9">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Refresh
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Every paid order. Click <strong>Send to CJ</strong> to create the order in your CJ account, then <strong>Pay on CJ</strong> to pay & ship it. Paste the tracking number and hit <strong>Mark shipped</strong> to auto-email the buyer.
        </p>

        <ShippingSettingsCard />

        {needsAction.length > 0 && (
          <div className="mb-4 text-sm bg-[#D9923B]/15 text-[#8a5a1a] rounded-lg px-4 py-2 font-medium" data-testid="fulfil-needs-action-banner">
            ⚡ {needsAction.length} order{needsAction.length > 1 ? "s" : ""} need you to send them to CJ.
          </div>
        )}

        {busy && orders.length === 0 ? (
          <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]" /></div>
        ) : orders.length === 0 ? (
          <Card className="p-10 text-center bg-white border-black/10">
            <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <div className="font-display text-xl">No orders yet</div>
            <div className="text-sm text-muted-foreground mt-1">Paid orders will appear here for you to fulfil.</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => <OrderCard key={o.supplier_order_id} order={o} onChanged={load} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default FulfilOrders;
